'use client'

/**
 * USASpending List Hooks
 *
 * Hooks for fetching paginated lists of USASpending entities.
 * These hooks seed detail caches from list data for instant navigation.
 */
import { useEffect } from 'react'

import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import { type ListParams, appDbKeys, usaspendingKeys } from './queryKeys'
import { useAwardQueries } from './queryOptions'

// API Response Types
interface AwardListItem {
  award_id: number
  display_award_id: string
  generated_unique_award_id: string
  category: string
  type: string
  type_description: string
  award_amount: number | null
  recipient_name: string | null
  recipient_hash: string | null
  recipient_uei: string | null
  recipient_levels: string[]
  parent_uei: string | null
  awarding_toptier_agency_name: string | null
  action_date: string | null
  fiscal_year: number | null
  description: string | null
}

interface ListResponse<T> {
  results: T[]
  page: number
  limit: number
  total: number
  has_next: boolean
}

// Fetch function factory
function createFetchFn<T>(endpoint: string) {
  return async (
    accessToken: string,
    params: ListParams,
  ): Promise<ListResponse<T>> => {
    const url = new URL(`${API_URL}/api/v1/usaspending/${endpoint}/`)
    url.searchParams.set('page', String(params.page ?? 1))
    url.searchParams.set('limit', String(params.limit ?? 25))
    if (params.sort) url.searchParams.set('sort', params.sort)
    if (params.order) url.searchParams.set('order', params.order)
    if (params.search) url.searchParams.set('search', params.search)
    if (params.category) url.searchParams.set('category', params.category)

    const res = await fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!res.ok) {
      throw new Error(`API error: ${res.status} ${res.statusText}`)
    }

    return res.json()
  }
}

// Recipient-scoped award list fetch (exported for CSV export)
export function fetchRecipientAwards(
  accessToken: string,
  recipientHash: string,
  params: ListParams,
): Promise<ListResponse<AwardListItem>> {
  const url = new URL(
    `${API_URL}/api/v1/usaspending/recipients/${recipientHash}/awards`,
  )
  url.searchParams.set('page', String(params.page ?? 1))
  url.searchParams.set('limit', String(params.limit ?? 25))
  if (params.sort) url.searchParams.set('sort', params.sort)
  if (params.order) url.searchParams.set('order', params.order)
  if (params.category) url.searchParams.set('category', params.category)

  return fetch(url.toString(), {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(`API error: ${res.status} ${res.statusText}`)
    }
    return res.json()
  })
}

// Fetch function factory for Application DB entities (no /usaspending/ prefix)
function createAppDbFetchFn<T>(endpoint: string) {
  return async (
    accessToken: string,
    params: ListParams,
  ): Promise<ListResponse<T>> => {
    const url = new URL(`${API_URL}/api/v1/${endpoint}/`)
    url.searchParams.set('page', String(params.page ?? 1))
    url.searchParams.set('limit', String(params.limit ?? 25))
    if (params.sort) url.searchParams.set('sort', params.sort)
    if (params.order) url.searchParams.set('order', params.order)
    if (params.search) url.searchParams.set('search', params.search)

    const res = await fetch(url.toString(), {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    })

    if (!res.ok) {
      throw new Error(`API error: ${res.status} ${res.statusText}`)
    }

    return res.json()
  }
}

/**
 * Hook to fetch an infinite-scrollable list of awards for a specific recipient.
 * Uses page-based pagination under the hood with useInfiniteQuery.
 * Seeds detail caches from list data for instant navigation.
 */
export function useRecipientAwardList(
  recipientHash: string,
  options: { enabled?: boolean; limit?: number; category?: string } = {},
) {
  const { enabled = true, limit = 25, category } = options
  const { accessToken } = useAuthInfo()
  const awardQueries = useAwardQueries()
  const queryClient = useQueryClient()

  type PageData = ListResponse<AwardListItem>

  const result = useInfiniteQuery({
    queryKey: usaspendingKeys.recipientAwardList(recipientHash, {
      limit,
      category,
    }),
    queryFn: ({ pageParam }) =>
      fetchRecipientAwards(accessToken ?? '', recipientHash, {
        page: pageParam,
        limit,
        sort: 'action_date',
        order: 'desc',
        category,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_next ? lastPage.page + 1 : undefined,
    enabled: enabled && !!accessToken && recipientHash.length > 0,
    staleTime: 5 * 60 * 1000,
    // When switching categories, instantly show client-filtered results from
    // the "All" cache while the server-filtered query loads in the background.
    placeholderData: () => {
      if (!category) return undefined
      const allKey = usaspendingKeys.recipientAwardList(recipientHash, {
        limit,
        category: undefined,
      })
      const cached =
        queryClient.getQueryData<InfiniteData<PageData, number>>(allKey)
      if (!cached) return undefined
      return {
        pages: cached.pages.map((page) => ({
          ...page,
          results: page.results.filter(
            (a) => a.category?.toLowerCase() === category,
          ),
        })),
        pageParams: cached.pageParams,
      }
    },
  })

  // Seed detail caches from list data for instant navigation
  useEffect(() => {
    if (result.data?.pages) {
      for (const page of result.data.pages) {
        awardQueries.seedManyFromList(page.results)
      }
    }
  }, [result.data, awardQueries])

  return result
}

// =============================================================================
// Application DB Entities (Federal Contracts, Grants, DIBBS)
// =============================================================================

interface FederalContractListItem {
  id: string
  title: string | null
  solicitation_number: string | null
  agency: string | null
  office: string | null
  notice_type: string | null
  posted_date: string | null
  response_deadline: string | null
  naics_code: string | null
  psc_code: string | null
  set_aside_code: string | null
  is_active: boolean
}

interface FederalGrantListItem {
  id: string
  opportunity_title: string | null
  opportunity_number: string | null
  agency_name: string | null
  post_date: string | null
  close_date: string | null
  funding_instrument_type: string | null
  category_of_funding_activity: string | null
  estimated_total_program_funding: number | null
  is_active: boolean
}

interface DibbsContractListItem {
  id: string
  title: string | null
  solicitation_number: string | null
  agency: string | null
  posted_date: string | null
  response_deadline: string | null
  naics_code: string | null
  psc_code: string | null
  is_active: boolean
}

interface SbirListItem {
  id: string
  title: string
  number: string
  program: string
  phase: string
  agency: string
  status: string
  release_date: string
  close_date: string
}

interface SledContractListItem {
  id: string
  title: string | null
  agency: string | null
  jurisdiction: string | null
  status: string | null
  posted_date: string
  due_date: string | null
}

// Fetch functions for Application DB entities (use createAppDbFetchFn for correct URL)
const fetchFederalContracts =
  createAppDbFetchFn<FederalContractListItem>('federal-contracts')
const fetchFederalGrants =
  createAppDbFetchFn<FederalGrantListItem>('federal-grants')
const fetchDibbs = createAppDbFetchFn<DibbsContractListItem>('dibbs-contracts')
const fetchSbirs = createAppDbFetchFn<SbirListItem>('sbirs')
const fetchSledContracts =
  createAppDbFetchFn<SledContractListItem>('sled-contracts')

/**
 * Hook to fetch a paginated list of federal contracts (Application DB)
 */
export function useFederalContractList(
  params: ListParams = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfoWithThrow()

  const queryParams: ListParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
    sort: params.sort ?? 'posted_date',
    order: params.order ?? 'desc',
  }

  return useQuery({
    queryKey: appDbKeys.federalContractList(queryParams),
    queryFn: () => fetchFederalContracts(accessToken, queryParams),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch a paginated list of federal grants (Application DB)
 */
export function useFederalGrantList(
  params: ListParams = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfoWithThrow()

  const queryParams: ListParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
    sort: params.sort ?? 'post_date',
    order: params.order ?? 'desc',
  }

  return useQuery({
    queryKey: appDbKeys.federalGrantList(queryParams),
    queryFn: () => fetchFederalGrants(accessToken, queryParams),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch a paginated list of DIBBS contracts (Application DB)
 */
export function useDibbsList(
  params: ListParams = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfoWithThrow()

  const queryParams: ListParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
    sort: params.sort ?? 'posted_date',
    order: params.order ?? 'desc',
  }

  return useQuery({
    queryKey: appDbKeys.dibbsContractList(queryParams),
    queryFn: () => fetchDibbs(accessToken, queryParams),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch a paginated list of SBIR opportunities (Application DB)
 */
export function useSbirList(
  params: ListParams = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfoWithThrow()

  const queryParams: ListParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
    sort: params.sort ?? 'release_date',
    order: params.order ?? 'desc',
  }

  return useQuery({
    queryKey: appDbKeys.sbirList(queryParams),
    queryFn: () => fetchSbirs(accessToken, queryParams),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to fetch a paginated list of SLED contracts (Application DB)
 */
export function useSledContractList(
  params: ListParams = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfoWithThrow()

  const queryParams: ListParams = {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
    sort: params.sort ?? 'posted_date',
    order: params.order ?? 'desc',
  }

  return useQuery({
    queryKey: appDbKeys.sledContractList(queryParams),
    queryFn: () => fetchSledContracts(accessToken, queryParams),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

// Export types for external use
export type {
  AwardListItem,
  FederalContractListItem,
  FederalGrantListItem,
  DibbsContractListItem,
  SbirListItem,
  SledContractListItem,
  ListResponse,
}
